import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { keys } from "../api/queryKeys.ts";
import { api } from "../api/client.ts";
import CountryName from "../components/CountryName.tsx";
import DistributionChart from "../components/DistributionChart.tsx";
import FormattedDate from "../components/FormattedDate.tsx";
import FilterBar from "../components/filters/FilterBar.tsx";
import SavedViewsMenu from "../components/filters/SavedViewsMenu.tsx";
import type { FilterOption, FilterSpec } from "../components/filters/specs.ts";
import { useFilters } from "../components/filters/useFilters.ts";
import { TitleLinkCell } from "../components/table/cells.tsx";
import EmptyTableRow from "../components/table/EmptyTableRow.tsx";
import ScrollTable from "../components/table/ScrollTable.tsx";
import SortableTableHead from "../components/table/SortableTableHead.tsx";
import TableCard from "../components/table/TableCard.tsx";
import UrlTablePagination from "../components/table/UrlTablePagination.tsx";
import { useOrganization } from "../contexts/OrganizationContext.tsx";
import { useCountryOptions } from "../hooks/useCountryOptions.ts";
import { useSourceStats } from "../hooks/useSourceStats.ts";
import { useCommaParam, useUrlPaging, useUrlSort, useUrlState } from "../hooks/useUrlState.ts";
import { formatNumber, humanize } from "../utils/format.ts";
import { sentimentColor } from "../utils/statusColors.ts";
import { parseFiniteNumber } from "../utils/urlFilters.ts";
import PageHeader from "../components/PageHeader.tsx";

const STATUS_OPTIONS: FilterOption[] = ["awaiting_content", "done", "stale"].map((s) => ({
  value: s,
  label: humanize(s),
}));

const plain = (values: string[] | undefined): FilterOption[] =>
  (values ?? []).map((v) => ({ value: v, label: v }));

const COLUMNS = [
  { id: "title", label: "Title" },
  { id: null, label: "Domain" },
  { id: null, label: "Country" },
  { id: null, label: "Language" },
  { id: "published_at", label: "Published" },
  { id: "sentiment_overall", label: "Sentiment" },
];

function NewsArticlesPage() {
  const navigate = useNavigate();
  const { currentOrg } = useOrganization();
  const url = useUrlState();
  const { searchParams } = url;

  const { data: filterOptions } = useQuery({
    queryKey: keys.newsArticle.filterOptions(),
    queryFn: () => api.getNewsArticleFilterOptions(),
    staleTime: 30 * 60 * 1000,
  });
  const countryOptions = useCountryOptions(filterOptions?.source_countries);

  const specs = useMemo<FilterSpec[]>(() => {
    const loading = !filterOptions;
    return [
      {
        kind: "options",
        key: "source_country",
        label: "Country",
        param: "source_country",
        pinned: true,
        loading,
        options: countryOptions,
        searchPlaceholder: "Search countries",
      },
      {
        kind: "dateRange",
        key: "published",
        label: "Published",
        pinned: true,
        fromParam: "published_from",
        toParam: "published_to",
      },
      { kind: "options", key: "language", label: "Language", param: "language", loading, options: plain(filterOptions?.languages) },
      {
        kind: "options",
        key: "domain",
        label: "Domain",
        param: "domain",
        loading,
        options: plain(filterOptions?.top_domains),
        searchPlaceholder: "Search domains",
      },
      {
        kind: "options",
        key: "keyword",
        label: "Keyword",
        param: "keyword",
        loading,
        options: plain(filterOptions?.top_keywords),
        searchPlaceholder: "Search keywords",
      },
      { kind: "options", key: "source", label: "Source", param: "source", loading, options: plain(filterOptions?.sources) },
      { kind: "options", key: "extraction_status", label: "Status", param: "extraction_status", options: STATUS_OPTIONS },
      {
        kind: "range",
        key: "sentiment",
        label: "Sentiment",
        minParam: "min_sentiment",
        maxParam: "max_sentiment",
        decimal: true,
      },
    ];
  }, [filterOptions, countryOptions]);

  const filters = useFilters(specs, {
    state: url,
    search: { param: "q", placeholder: "Search titles" },
    pageKey: "news-articles",
    scopeId: null,
    remember: true,
  });

  const sources = useCommaParam(searchParams, "source");
  const countries = useCommaParam(searchParams, "source_country");
  const languages = useCommaParam(searchParams, "language");
  const domains = useCommaParam(searchParams, "domain");
  const keywords = useCommaParam(searchParams, "keyword");
  const statuses = useCommaParam(searchParams, "extraction_status");
  const param = (key: string) => searchParams.get(key) || undefined;

  const { sortBy, sortDir, handleSort } = useUrlSort(url, "published_at", "desc");
  const paging = useUrlPaging(url);

  const queryParams = {
    q: param("q"),
    source: sources.length ? sources : undefined,
    source_country: countries.length ? countries : undefined,
    language: languages.length ? languages : undefined,
    domain: domains.length ? domains : undefined,
    keyword: keywords.length ? keywords : undefined,
    extraction_status: statuses.length ? statuses : undefined,
    published_from: param("published_from"),
    published_to: param("published_to"),
    min_sentiment: parseFiniteNumber(searchParams.get("min_sentiment") ?? ""),
    max_sentiment: parseFiniteNumber(searchParams.get("max_sentiment") ?? ""),
    sort_by: sortBy,
    sort_dir: sortDir,
    limit: paging.rowsPerPage,
    offset: paging.offset,
  };

  const { data, isLoading } = useQuery({
    queryKey: keys.newsArticle.list(queryParams),
    queryFn: () => api.getNewsArticles(queryParams),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const stats = useSourceStats(keys.newsArticle.stats(), api.getNewsArticleStats, (s) => ({
    label: s.source,
    count: s.count,
  }));

  return (
    <>
      <PageHeader title="News articles" description="Articles collected from news feeds and news APIs" />
      <DistributionChart
        title="Articles per source"
        data={stats.chartData}
        loading={stats.isLoading}
      />
      <TableCard
        loading={isLoading}
        toolbar={
          <FilterBar
            filters={filters}
            count={data ? `${formatNumber(data.total)} ${data.total === 1 ? "article" : "articles"}` : undefined}
            views={
              <SavedViewsMenu
                filters={filters}
                context={{ organizationId: currentOrg?.id, organizationName: currentOrg?.name }}
              />
            }
          />
        }
        footer={data && data.total > 0 && <UrlTablePagination total={data.total} paging={paging} />}
      >
        <ScrollTable>
          <SortableTableHead
            columns={COLUMNS}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
          />
          <TableBody>
            {data?.items.map((a) => (
              <TableRow
                key={a.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => navigate(`/news-articles/${a.id}`)}
              >
                <TitleLinkCell
                  title={
                    a.title ?? (
                      <Box component="em" sx={{ color: "text.disabled" }}>
                        (no title)
                      </Box>
                    )
                  }
                  href={a.document_url}
                />
                <TableCell>{a.source_domain ?? a.source_common_name ?? "-"}</TableCell>
                <TableCell>
                  {a.source_country ? <CountryName code={a.source_country} /> : "-"}
                </TableCell>
                <TableCell>{a.language ?? "-"}</TableCell>
                <TableCell>
                  <FormattedDate value={a.published_at} />
                </TableCell>
                <TableCell>
                  {a.sentiment_overall != null ? (
                    <Chip
                      size="small"
                      label={a.sentiment_overall.toFixed(2)}
                      color={sentimentColor(a.sentiment_overall)}
                      variant="outlined"
                    />
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
            ))}
            {data?.items.length === 0 && (
              <EmptyTableRow colSpan={COLUMNS.length}>
                {filters.activeCount > 0 ? "No articles match your filters" : "No articles found"}
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      </TableCard>
    </>
  );
}

export default NewsArticlesPage;
